import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TextFieldModule } from '@angular/cdk/text-field';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	ElementRef,
	HostBinding,
	HostListener,
	inject,
	Inject,
	NgZone,
	OnDestroy,
	OnInit,
	Renderer2,
	signal,
	viewChild,
	ViewChild,
	ViewEncapsulation,
	DOCUMENT,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { filter, first, firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
	HubConnection,
	HubConnectionBuilder,
	LogLevel,
} from '@microsoft/signalr';
import { environment } from 'environments/environment';
import {
	AssistantActionTypeEnum,
	AssistantMessageDto,
	MessageRole,
} from 'app/core/services/api.service';
import { FormsModule } from '@angular/forms';
import {
	MarkdownComponent,
	MarkdownModule,
	provideMarkdown,
} from 'ngx-markdown';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { UserSettingsService } from 'app/core/services/user-settings.service';
import { MinimumTierDirective } from 'app/directives/minimum-tier.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { ClaimService } from 'app/modules/pages/claims/claim.service';
import { UserSettingsStore } from 'app/store/user-setting.store';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { AuthService } from '@auth0/auth0-angular';

@Component({
	selector: 'ai-chat',
	templateUrl: './ai-chat.component.html',
	styleUrls: ['./ai-chat.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	exportAs: 'aiChat',
	imports: [
		FormsModule,
		NgClass,
		MatIconModule,
		MatButtonModule,
		NgTemplateOutlet,
		MatFormFieldModule,
		MatInputModule,
		TextFieldModule,
		MarkdownModule,
		MinimumTierDirective,
		MatTooltipModule,
	],
})
export class AiChatComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('messageInput') messageInput: ElementRef;

	opened = signal<boolean>(false);
	isScreenSmall = signal<boolean>(false);

	isOpen = computed(() => this.opened() && !this.isScreenSmall());

	private _userSettingsStore = inject(UserSettingsStore);
	private _mutationObserver: MutationObserver;
	private _scrollStrategy: ScrollStrategy =
		this._scrollStrategyOptions.block();
	private _unsubscribeAll: Subject<any> = new Subject<any>();

	chatContainer = viewChild<ElementRef<HTMLDivElement> | undefined>(
		'chatContainer'
	);

	private hubConnection: HubConnection;

	threadId: string;
	message: string = '';
	messages: AssistantMessageDto[] = [];
	messageRoles = MessageRole;
	actionType = AssistantActionTypeEnum;

	@ViewChild(MarkdownComponent) markdown: MarkdownComponent;

	/**
	 * Constructor
	 */
	constructor(
		@Inject(DOCUMENT) private _document: Document,
		private _elementRef: ElementRef,
		private _renderer2: Renderer2,
		private _ngZone: NgZone,
		private _scrollStrategyOptions: ScrollStrategyOptions,
		private _router: Router,
		private _userService: UserService,
		private _userSettingsService: UserSettingsService,
		private _fuseConfirmationService: FuseConfirmationService,
		private _claimService: ClaimService,
		private _changeDetectorRef: ChangeDetectorRef,
		private _fuseMediaWatcherService: FuseMediaWatcherService,
		private _auth0: AuthService
	) {
		let assistantOpened =
			this._userSettingsStore.getUserSettingByKey('sidebar-assistant');

		if (!assistantOpened) {
			assistantOpened = '1';
		}

		this._toggleOpened(assistantOpened === '1');
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Decorated methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Host binding for component classes
	 */
	@HostBinding('class') get classList(): any {
		return {
			'ai-chat-opened': this.opened(),
		};
	}

	/**
	 * Resize on 'input' and 'ngModelChange' events
	 *
	 * @private
	 */
	@HostListener('input')
	@HostListener('ngModelChange')
	_resizeMessageInput(): void {
		// This doesn't need to trigger Angular's change detection by itself
		this._ngZone.runOutsideAngular(() => {
			setTimeout(() => {
				// Set the height to 'auto' so we can correctly read the scrollHeight
				this.messageInput.nativeElement.style.height = 'auto';

				// Get the scrollHeight and subtract the vertical padding
				this.messageInput.nativeElement.style.height = `${this.messageInput.nativeElement.scrollHeight}px`;
			});
		});
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	async ngOnInit(): Promise<void> {
		await this.startSignalRConnection();
		this.setupSignalRListeners();

		let assistantThreadId =
			this._userSettingsStore.getUserSettingByKey('assistant-threadid');

		if (assistantThreadId) {
			this.threadId = assistantThreadId;
			await this.hubConnection.invoke<AssistantMessageDto>(
				'GetMessages',
				this.threadId
			);
		}

		this._claimService.claimNumber
			.pipe(
				filter(
					(claimNumber) =>
						claimNumber !== null && this.opened() === true
				),
				takeUntil(this._unsubscribeAll)
			)
			.subscribe({
				next: (claimNumber) => {
					const userMessage = AssistantMessageDto.fromJS({
						id: '',
						role: this.messageRoles.User,
						content: `Claim ${claimNumber}`,
					});

					this.sendMessageToHub(userMessage, 'AddClaimToChat');
				},
			});

		this._fuseMediaWatcherService.onMediaChange$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(({ matchingAliases }) => {
				// Check if the screen is small
				const isScreenSmall = !matchingAliases.includes('md');

				// Change the navigation appearance
				this.isScreenSmall.set(isScreenSmall);
			});
	}

	/**
	 * After view init
	 */
	ngAfterViewInit(): void {
		// Fix for Firefox.
		//
		// Because 'position: sticky' doesn't work correctly inside a 'position: fixed' parent,
		// adding the '.cdk-global-scrollblock' to the html element breaks the navigation's position.
		// This fixes the problem by reading the 'top' value from the html element and adding it as a
		// 'marginTop' to the navigation itself.
		this._mutationObserver = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				const mutationTarget = mutation.target as HTMLElement;
				if (mutation.attributeName === 'class') {
					if (
						mutationTarget.classList.contains(
							'cdk-global-scrollblock'
						)
					) {
						const top = parseInt(mutationTarget.style.top, 10);
						this._renderer2.setStyle(
							this._elementRef.nativeElement,
							'margin-top',
							`${Math.abs(top)}px`
						);
					} else {
						this._renderer2.setStyle(
							this._elementRef.nativeElement,
							'margin-top',
							null
						);
					}
				}
			});
		});
		this._mutationObserver.observe(this._document.documentElement, {
			attributes: true,
			attributeFilter: ['class'],
		});
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		if (this.threadId)
			this._userSettingsService.setByKeyValue(
				'assistant-threadid',
				this.threadId
			);

		this.hubConnection.stop();

		// Disconnect the mutation observer
		this._mutationObserver.disconnect();

		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	handleClick(event: MouseEvent) {
		// Check if clicked element is our markdown link
		const target = event.target as HTMLElement;
		if (target.classList.contains('markdown-link')) {
			event.preventDefault();
			const href = target.getAttribute('href');
			this._router.navigate([href]);
		}
	}

	/**
	 * Open the panel
	 */
	open(): void {
		// Return if the panel has already opened
		if (this.opened()) {
			return;
		}

		this._userSettingsService.setByKeyValue('sidebar-assistant', '1');

		// Open the panel
		this._toggleOpened(true);
	}

	/**
	 * Close the panel
	 */
	close(): void {
		// Return if the panel has already closed
		if (!this.opened()) {
			return;
		}

		this._userSettingsService.setByKeyValue('sidebar-assistant', '0');

		// Close the panel
		this._toggleOpened(false);
	}

	/**
	 * Toggle the panel
	 */
	toggle(): void {
		if (this.opened()) {
			this.close();
		} else {
			this.open();
		}
	}

	/**
	 * Track by function for ngFor loops
	 *
	 * @param index
	 * @param item
	 */
	trackByFn(index: number, item: any): any {
		return item.id || index;
	}

	openDetail(id: string): void {
		if (this._userService.hasPermission(['claim:view', 'claim:allaccess']))
			this._router.navigateByUrl(`/claims/${id}/overview`);
	}

	onNewChat() {
		// Open the confirmation dialog
		const confirmation = this._fuseConfirmationService.open({
			title: 'New Chat',
			message: `Are you sure you want to start a new chat?`,
		});

		confirmation
			.afterClosed()
			.pipe(first())
			.subscribe(async (result) => {
				if (result === 'cancelled') return;

				this.threadId = await this.hubConnection.invoke<string>(
					'CreateThread'
				);
				this.messages = []; // Clear existing messages to start a new chat
				this.message = ''; // Clear the message input field

				this._changeDetectorRef.detectChanges();

				this._userSettingsService.setByKeyValue(
					'assistant-threadid',
					this.threadId
				);
			});
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------

	private async startSignalRConnection(): Promise<void> {
		try {
			this.hubConnection = new HubConnectionBuilder()
				.withUrl(`${environment.apiUrl}/hubs/assistant`, {
					accessTokenFactory: () =>
						firstValueFrom(this._auth0.getAccessTokenSilently()),
				})
				.withAutomaticReconnect()
				.withStatefulReconnect()
				.configureLogging(
					environment.env == 'dev' ? LogLevel.None : LogLevel.Trace
				)
				.build();

			await this.hubConnection.start();
		} catch (err) {
			console.error('Error while starting SignalR connection:', err);
		}
	}

	private setupSignalRListeners(): void {
		this.hubConnection.on(
			'ReceiveMessage',
			(message: AssistantMessageDto) => {
				const existingMessageIndex = this.messages.findIndex(
					(m) => m.id === message.id
				);

				if (existingMessageIndex !== -1) {
					this.messages[existingMessageIndex] = message;
				} else {
					if (message.role === this.messageRoles.User) {
						let pendingUserMessage = this.messages.find(
							(m) => m.role === this.messageRoles.User && !m.id
						);
						if (pendingUserMessage) {
							pendingUserMessage.id = message.id;
						}
					} else {
						// Only remove pending message if this is an assistant message
						const pendingIndex = this.messages.findIndex(
							(m) => m.id && m.id.startsWith('pending-')
						);
						if (pendingIndex !== -1) {
							this.messages.splice(pendingIndex, 1);
						}
						this.messages.push(message);
					}
				}

				this._changeDetectorRef.detectChanges();

				setTimeout(() => this.scrollToBottom(), 10);
			}
		);

		this.hubConnection.on(
			'ReceiveMessages',
			(message: AssistantMessageDto[]) => {
				this.messages = message;
				this._changeDetectorRef.detectChanges();

				setTimeout(() => this.scrollToBottom(), 10);
			}
		);

		// Handle streaming updates
		this.hubConnection.on(
			'ReceiveStreamingUpdate',
			(update: AssistantMessageDto) => {
				const pendingIndex = this.messages.findIndex(
					(m) => m.id && m.id.startsWith('pending-')
				);

				if (pendingIndex === -1) {
					// Create new pending message
					const pendingMessage = AssistantMessageDto.fromJS({
						id: 'pending-' + update.id,
						role: this.messageRoles.Assistant,
						content: update.content,
						createdAt: update.createdAt,
						actionType: update.actionType,
						actionName: update.actionName,
						ids: update.ids,
					});
					this.messages.push(pendingMessage);
				} else {
					// Update existing pending message content
					const currentMessage = this.messages[pendingIndex];
					// If this is the first streaming update, replace "Thinking..." with the actual content
					const newContent =
						currentMessage.content === 'Thinking...'
							? update.content
							: currentMessage.content + update.content;

					this.messages[pendingIndex] = AssistantMessageDto.fromJS({
						...currentMessage,
						content: newContent,
					});
				}

				this._changeDetectorRef.detectChanges();
				this.scrollToBottom();
			}
		);
	}

	private scrollToBottom(): void {
		try {
			const container = this.chatContainer();
			if (container) {
				container.nativeElement.scrollTop =
					container.nativeElement.scrollHeight;
			}
		} catch (err) {}
	}

	async sendMessage() {
		if (this.message.trim() === '') return;

		const userMessage = AssistantMessageDto.fromJS({
			id: '',
			role: this.messageRoles.User,
			content: this.message,
		});
		this.messages.push(userMessage);

		this._changeDetectorRef.detectChanges();

		if (!this.threadId) {
			try {
				this.threadId = await this.hubConnection.invoke<string>(
					'CreateThread'
				);
				this._userSettingsService.setByKeyValue(
					'assistant-threadid',
					this.threadId
				);

				this.sendMessageToHub(userMessage);
			} catch (err) {
				console.error('Error creating thread:', err);
			}
		} else {
			this.sendMessageToHub(userMessage);
		}

		this.message = ''; // Clear the input field

		// setTimeout(() => this.scrollToBottom(), 0);
	}

	async sendMessageToHub(
		userMessage: AssistantMessageDto,
		method: string = 'SendMessage'
	) {
		if (!this.threadId) return;

		const pendingMessage = AssistantMessageDto.fromJS({
			id: 'pending-' + Date.now().toString(),
			role: this.messageRoles.Assistant,
			content: 'Thinking...',
		});
		if (method == 'SendMessage') this.messages.push(pendingMessage);

		try {
			await this.hubConnection.invoke<AssistantMessageDto>(
				method,
				this.threadId,
				userMessage.content
			);
			// The response will be handled by the SignalR listener (ReceiveMessage)
		} catch (err) {
			console.error('Error sending message:', err);
			// Handle error (e.g., remove pending message, show error to user)
			if (method !== 'SendMessage') return;

			const errorIndex = this.messages.findIndex(
				(m) => m.id === pendingMessage.id
			);
			if (errorIndex !== -1) {
				this.messages.splice(errorIndex, 1);
			}
			this.messages.push(
				AssistantMessageDto.fromJS({
					role: this.messageRoles.Assistant,
					content:
						'An error occurred while processing your message. Please try again.',
				})
			);
		}

		this._changeDetectorRef.detectChanges();
	}

	/**
	 * Open/close the panel
	 *
	 * @param open
	 * @private
	 */
	private _toggleOpened(open: boolean): void {
		// Set the opened
		this._userSettingsService.setByKeyValue(
			'sidebar-assistant',
			open ? '1' : '0'
		);
		this.opened.set(open);
	}
}
